import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Home } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { StatusBadge, Empty } from "@/components/ui-craft";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export default function ClientDashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const [reqs, setReqs] = useState(null);
  useEffect(() => { api.get("/requests").then((r) => setReqs(r.data)); }, []);
  const open = reqs?.filter((r) => !["chiuso", "annullato", "rifiutato"].includes(r.status)) || [];
  const closed = reqs?.filter((r) => ["chiuso", "annullato", "rifiutato"].includes(r.status)) || [];
  const action = (r) => t("client.actions")[r.status];

  return (
    <Shell>
      <div className="flex flex-wrap items-end justify-between gap-4 rise">
        <div><p className="eyebrow">{t("client.area")}</p><h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold">{t("client.hi", { name: user.name.split(" ")[0] })}</h1></div>
        <div className="flex flex-wrap gap-2">
          <Link to="/app/casa" data-testid="my-home-button" className="btn-secondary"><Home size={16} /> {t("nav.myHome")}</Link>
          <Link to="/app/richiesta/segnala-problema" data-testid="new-request-button" className="btn-primary"><Plus size={16} /> {t("cta.requestIntervention")}</Link>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="text-lg font-semibold">{t("client.inProgress")} <span className="font-mono text-ink/40 text-sm ml-2">{open.length}</span></h2>
        <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="open-requests">
          {reqs && open.length === 0 && <div className="md:col-span-2"><Empty title={t("client.noActive")}>{t("client.noActiveHint")}</Empty></div>}
          {open.map((r, i) => (
            <Link to={`/app/richieste/${r.id}`} key={r.id} data-testid={`request-card-${r.number}`} className={`card-craft p-6 flex flex-col gap-3 transition-transform duration-200 hover:-translate-y-0.5 rise rise-${(i % 4) + 1}`}>
              <div className="flex items-start justify-between gap-3"><span className="font-mono text-xs text-ink/50">{r.number}</span><StatusBadge status={r.status} /></div>
              <h3 className="font-serif text-2xl font-semibold">{r.service_name}</h3>
              <p className="text-sm text-ink/60">{r.address}, {r.city} · {fmtDate(r.created_at)}</p>
              {action(r) && <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-terracotta" data-testid="request-action-hint">{action(r)} <ArrowRight size={14} /></span>}
            </Link>
          ))}
        </div>
      </section>

      {closed.length > 0 && (
        <section className="mt-14">
          <h2 className="text-lg font-semibold">{t("client.history")}</h2>
          <div className="mt-4 card-craft divide-y divide-craft" data-testid="closed-requests">
            {closed.map((r) => (
              <Link to={`/app/richieste/${r.id}`} key={r.id} className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-linen transition-colors">
                <div><div className="font-semibold">{r.service_name}</div><div className="text-xs text-ink/50 font-mono">{r.number} · {fmtDate(r.created_at)}</div></div>
                <StatusBadge status={r.status} />
              </Link>
            ))}
          </div>
        </section>
      )}
    </Shell>
  );
}
